import { $items } from '@/utils/store';
import { useStore } from '@nanostores/react';
import { atom } from 'nanostores';

export type Selection = {
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
};

export const $startSelection = atom<{
  row: number;
  col: number;
} | null>(null);
export const $selection = atom<Selection | null>(null);

export function useInSelection(row: number, col: number) {
  const selection = useStore($selection);
  if (!selection) return false;

  const { startRow, startCol, endRow, endCol } = selection;

  const rowInRange = row >= startRow && row <= endRow;
  const colInRange = col >= startCol && col <= endCol;

  return rowInRange && colInRange;
}

export function clearSelection() {
  $startSelection.set(null);
  $selection.set(null);
}

export function startSelection(row: number, col: number) {
  $startSelection.set({ row, col });
  $selection.set(null);
}

export function endSelection(row: number, col: number) {
  const start = $startSelection.get();
  if (!start) return;

  const startRow = Math.min(start.row, row);
  const endRow = Math.max(start.row, row);
  const startCol = Math.min(start.col, col);
  const endCol = Math.max(start.col, col);

  $selection.set({ startRow, startCol, endRow, endCol });

  // Clear selection and unfocus any elements
  window.getSelection()?.removeAllRanges();
  (document.activeElement as HTMLElement)?.blur();
}

function onKeyDown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    clearSelection();
  }
}

function onCopy(e: ClipboardEvent) {
  const selection = $selection.get();
  if (!selection) return;
  const items = $items.get();

  const { startRow, startCol, endRow, endCol } = selection;

  const rows = [];
  for (let row = startRow; row <= endRow; row++) {
    const item = items[row];
    if (!item) continue;

    const cols = [];
    const serialsRow = [];
    let hasSerials = false;
    for (let col = startCol; col <= endCol; col++) {
      if (col === 0) {
        cols.push(item.stockNumber);
        serialsRow.push('');
      } else if (col === 1) {
        cols.push(item.description);
        serialsRow.push(
          item.serials &&
            item.serials.length &&
            item.serials.map(t => t.trim()).join(', '),
        );
        hasSerials = !!(item.serials && item.serials.length);
      } else {
        const quantity = item.quantities[col - 2];
        cols.push(quantity !== undefined && quantity !== null ? quantity : '');
        serialsRow.push('');
      }
    }
    rows.push(cols.join('\t'));
    if (hasSerials) {
      rows.push(serialsRow.join('\t'));
    }
  }

  e.clipboardData?.setData('text/plain', rows.join('\n'));
  e.preventDefault();
}

window.addEventListener('keydown', onKeyDown);
window.addEventListener('mousedown', e => {
  if (e.button !== 0) return;
  if (e.target instanceof HTMLElement && e.target.closest('.selectable-item'))
    return;
  clearSelection();
});
window.addEventListener('copy', onCopy);
